/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: inherit
 */
import React from 'react';
import { StyleSheet, Text, View } from 'react-native';
import { spacing } from '../../shared/theme/spacing'
import { typography } from '../../shared/theme/typography'
import { ticketColors } from '../../shared/theme/ticketColors';

export type AgingBuckets = {
  bucket_0_30: number;
  bucket_31_60: number;
  bucket_61_90: number;
  bucket_90_plus: number;
};

type Props = {
  buckets: AgingBuckets;
  formatMoney: (n: number) => string;
};

const BUCKET_DEFS: { key: keyof AgingBuckets; label: string; color: string }[] = [
  { key: 'bucket_0_30', label: '0–30 días', color: ticketColors.ok },
  { key: 'bucket_31_60', label: '31–60 días', color: ticketColors.gold },
  { key: 'bucket_61_90', label: '61–90 días', color: ticketColors.gold },
  { key: 'bucket_90_plus', label: '+90 días', color: ticketColors.bad },
];

export function CustomerAgingBreakdown({ buckets, formatMoney }: Props) {
  const total = BUCKET_DEFS.reduce((sum, b) => sum + buckets[b.key], 0);

  if (total <= 0) {
    return (
      <View style={styles.emptyContainer}>
        <Text style={styles.emptyText}>No hay deuda para armar por fecha.</Text>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <Text style={styles.title}>Deuda por fecha</Text>
      {BUCKET_DEFS.map(({ key, label, color }) => {
        const amount = buckets[key];
        const pct = total > 0 ? amount / total : 0;

        return (
          <View key={key} style={styles.row}>
            <View style={styles.labelRow}>
              <Text style={styles.bucketLabel}>{label}</Text>
              <Text style={[styles.bucketAmount, amount > 0 && styles.bucketAmountActive]}>
                {formatMoney(amount)}
              </Text>
            </View>
            <View style={styles.barTrack}>
              <View
                style={[
                  styles.barFill,
                  {
                    width: `${Math.round(pct * 100)}%` as any,
                    backgroundColor: color,
                    opacity: amount > 0 ? 1 : 0.2,
                  },
                ]}
              />
            </View>
          </View>
        );
      })}
      <View style={styles.totalRow}>
        <Text style={styles.totalLabel}>Total</Text>
        <Text style={styles.totalAmount}>{formatMoney(total)}</Text>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    gap: spacing.sm,
  },
  emptyContainer: {
    paddingVertical: spacing.sm,
  },
  emptyText: {
    ...typography.caption,
    color: ticketColors.ink3,
    fontStyle: 'italic',
  },
  title: {
    ...typography.caption,
    color: ticketColors.ink2,
    fontWeight: '900',
    textTransform: 'uppercase',
    letterSpacing: 0.35,
  },
  row: {
    gap: 4,
  },
  labelRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  bucketLabel: {
    ...typography.caption,
    color: ticketColors.ink2,
    fontWeight: '600',
  },
  bucketAmount: {
    ...typography.caption,
    color: ticketColors.ink3,
    fontWeight: '700',
  },
  bucketAmountActive: {
    color: ticketColors.ink,
  },
  barTrack: {
    height: 6,
    borderRadius: 3,
    backgroundColor: ticketColors.rule,
    overflow: 'hidden',
  },
  barFill: {
    height: 6,
    borderRadius: 3,
  },
  totalRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingTop: spacing.xs,
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: ticketColors.rule,
  },
  totalLabel: {
    ...typography.caption,
    color: ticketColors.ink2,
    fontWeight: '800',
    textTransform: 'uppercase',
    letterSpacing: 0.3,
  },
  totalAmount: {
    ...typography.body,
    color: ticketColors.ink,
    fontWeight: '800',
  },
});
